// @flow
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import isEmpty from 'ramda/es/isEmpty';
import path from 'ramda/es/path';
import find from 'ramda/es/find';
import prop from 'ramda/es/prop';

import RowLayout, { Header } from 'app/common/layouts/RowLayout';
import ProductGallery, { ProductGalleryThumb } from 'app/common/components/ProductGallery';
// import Pill, { PillGroup } from 'app/common/ui/Pill';
import { Tabs, Tab, TabPanels, TabPanel, TabsVertical } from 'app/common/ui/PlainTabs';
// $FlowFixMe
import { productFetch } from 'app/common/actions/shop/catalog/product';
import Loading from 'app/common/components/Loader';
import { LocalBreadcrumb, Add2CartButton } from 'app/common/components/Shop';
import { HR } from 'app/common/layouts/markup';

import {
  ProductDetailsGrid,
  ProductDetailsColumn,
  ProductDetailsHeader,
  ProductDetailsPrice,
  SpecificationsTable as SP,
} from 'app/common/layouts/catalog';

import selectProduct from './selectProduct';
import DeliveryHighlight from './DeliveryHighlight';
import styles from './ProductDetails.module.scss';;


type Props = {
  baseUrl: string,
  params: {
    splat?: string,
    productSlug: string,
    section: 'catalog',
  },
  push: (path: string) => void,
}

const ProductDetails = ({ params, baseUrl, push }: Props) => {
  const { productSlug, splat } = params;
  const dispatch = useDispatch();

  // запрос категорий и товаров каталога
  const handleRequest = useCallback(async () => {
    const res = await dispatch(productFetch(productSlug));
    if (res.errStatus === 404) {
      push('/shop');
    }
  }, [dispatch, productSlug, push]);

  useEffect(() => {
    handleRequest();
  }, [handleRequest]);

  // данные товара
  const product = useSelector(selectProduct);

  // процесс запроса данных
  const loading = useSelector(state => state.shop.catalog.product.loading, shallowEqual);

  // показывать ли таб со свойствами
  const isProperies = !!(product.properties && !isEmpty(product.properties));

  // управление изображениями
  const [currentPicId, setCurrentPicId] = useState(path(['images', 0, 'id'], product));
  useEffect(() => {
    setCurrentPicId(path(['images', 0, 'id'], product));
  }, [product]);

  // управление вариантами
  // const [currentColorId, setCurrentColorId] = useState('violet');

  // управление табами
  const [activeTabIndex, setActiveTabIndex] = useState(1);
  const handleChangeTab = useCallback(index => setActiveTabIndex(index), []);

  // текущее 'большое' изображение
  const currentImageUrl = useMemo(() => {
    const need2x = window.devicePixelRatio > 1;
    if (!isEmpty(product.images)) {
      const selected = find(({ id }) => id === currentPicId, product.images) || {};
      return prop(need2x ? 'high' : 'standart', selected);
    }
    return '';
  }, [product, currentPicId]);

  if (loading) return (<Loading />);

  return (
    <React.Fragment>
      <RowLayout>
        <Header>{product.title}</Header>
      </RowLayout>
      <RowLayout className={styles.rowLayout2}>
        <LocalBreadcrumb
          // lastTitle={product.title}
          baseUrl={`${baseUrl}/category`}
          splat={splat}
        />
        <ProductDetailsGrid>
          <ProductDetailsColumn>
            {!isEmpty(product.images)
              && <ProductGallery imgSrc={currentImageUrl} vertical>
                {product.images.map(({ id, thumb }) => (
                  <ProductGalleryThumb
                    key={id}
                    id={id}
                    imgSrc={thumb}
                    active={currentPicId === id}
                    onClick={setCurrentPicId}
                  />
                ))}
              </ProductGallery>}
          </ProductDetailsColumn>
          <ProductDetailsColumn>
            <ProductDetailsHeader>{product.title}</ProductDetailsHeader>
            {/* Варианты цвета */}
            {/* <PillGroup id="colorSwitcher" label="Цвет товара:">
              <Pill
                id="violet"
                primary={currentColorId === 'violet'}
                outline={currentColorId !== 'violet'}
                onClick={setCurrentColorId}
              >
                {'Фиолетовый'}
              </Pill>
              <Pill
                id="green"
                primary={currentColorId === 'green'}
                outline={currentColorId !== 'green'}
                onClick={setCurrentColorId}
              >
                {'Зеленый'}
              </Pill>
              <Pill
                id="blue"
                primary={currentColorId === 'blue'}
                outline={currentColorId !== 'blue'}
                onClick={setCurrentColorId}
              >
                {'Синий'}
              </Pill>
              <Pill
                id="red"
                primary={currentColorId === 'red'}
                outline={currentColorId !== 'red'}
                onClick={setCurrentColorId}
              >
                {'Красный'}
              </Pill>
            </PillGroup> */}
            {/* Цена */}
            <ProductDetailsPrice value={product.price} />
            {/* Кнопка добавления в корзину */}
            <Add2CartButton productId={product.id} />

            <DeliveryHighlight className={styles.highlight} />

          </ProductDetailsColumn>
        </ProductDetailsGrid>

        <HR />

        <TabsVertical>
          <Tabs value={activeTabIndex} onChange={handleChangeTab} vertical>
            <Tab index={1}>{'Описание'}</Tab>
            {isProperies && <Tab index={2}>{'Характеристики'}</Tab>}
            {/* <Tab index={3}>Отзывы (15)</Tab> */}
          </Tabs>
          <TabPanels value={activeTabIndex}>
            <TabPanel index={1} className={styles.description}>
              <div
                className={styles.tabPanelContent}
                dangerouslySetInnerHTML={{ __html: product.description }} // eslint-disable-line react/no-danger
              />
            </TabPanel>
            {isProperies
              && <TabPanel index={2} className={styles.properties}>
                <SP.Table className={styles.propertiesTable}>
                  {product.properties.map(({ id, presentation, value }) => (
                    <SP.Row key={id}>
                      <SP.Cell>{presentation}</SP.Cell>
                      <SP.Cell>{value}</SP.Cell>
                    </SP.Row>
                  ))}
                </SP.Table>
              </TabPanel>}
            {/* <TabPanel index={3}>
              3
            </TabPanel> */}
          </TabPanels>
        </TabsVertical>
      </RowLayout>
    </React.Fragment>
  );
};

export default ProductDetails;
